import AdyenWebCheckout from '@adyen/adyen-web'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
// $FlowFixMe
import '@adyen/adyen-web/dist/adyen.css'
import { Spacer } from 'app-v2/design-systems/components/Spacer/Spacer'
import { colors } from 'app-v2/design-systems/theme'
import { COLOR, SIZE, SPACING } from 'app-v2/design-tokens'
import { CreditCard } from 'app/assets/icons'
import { Collapsible } from 'app/common/collapsible'
import { Radio } from 'app/common/forms'
import { H4, Text, fonts } from 'app/common/typography'
import type { FormikProps } from 'formik'
import type { Dispatch } from 'react'
import { IntlShape } from 'react-intl'
import { colors as oldColors } from 'shared/assets/styles'
import styled from 'styled-components'
import { type StoredPaymentMethod } from 'shared/types'
import { FeatureToggle } from 'app/common/feature-toggle'
import { featureCodes } from 'shared/utils'

type AdyenCheckoutProps = {
    formik: FormikProps,
    setError: Dispatch<{ message?: String, reason?: string }>,
    intl: IntlShape,
    storedCards?: StoredPaymentMethod[]
}

export const AdyenCheckout = ({
    formik,
    setError,
    storedCards = [],
    intl
}: AdyenCheckoutProps) => {
    const { setFieldValue, values } = formik
    const paymentContainer = useRef(null)
    const { authenticated } = useSelector(state => state.userState)
    const orderState = useSelector(state => state?.ecommerceOrderState)
    const [checkout, setCheckout] = useState(null)
    const [isCollapsed, setIsCollapsed] = useState(
        !!orderState.adyen.paymentMethods?.storedPaymentMethods
    )
    const [cardIdSelected, setCardIdSelected] = useState('')
    const usableCardIds = storedCards.map(card => card.id)

    const AdyenConfiguration = {
        locale: 'fr-FR',
        environment: process.env.REACT_APP_ADYEN_ENVIRONNEMENT,
        clientKey: process.env.REACT_APP_ADYEN_CLIENT_KEY,
        onChange: value => {
            if (value.isValid) {
                setFieldValue('adyenData', { ...value.data, isValid: true })
            } else {
                setFieldValue('adyenData', {
                    ...values.adyenData,
                    isValid: false
                })
            }
        },
        paymentMethodsResponse: orderState.adyen.paymentMethods,
        onError: (error, _component) => {
            setError({ message: error.message })
        }
    }

    useEffect(() => {
        if (checkout) return
        const createCheckout = async () => {
            const adyenCheckout = await AdyenWebCheckout({
                ...AdyenConfiguration
            })
            setCheckout(adyenCheckout)
        }

        createCheckout()
    }, [orderState.adyen, checkout])

    useEffect(() => {
        if (!paymentContainer.current || !checkout) return
        checkout
            .create('card', {
                hasHolderName: true,
                holderNameRequired: true,
                positionHolderNameOnTop: true,
                enableStoreDetails: authenticated
            })
            .mount(paymentContainer.current)
    }, [paymentContainer.current, checkout])

    useEffect(() => {
        if (!checkout || !checkout.paymentMethodsResponse.storedPaymentMethods)
            return

        //$FlowFixMe
        const storedPaymentMethods = checkout.paymentMethodsResponse.storedPaymentMethods?.filter(
            storedPaymentMethod =>
                usableCardIds.includes(storedPaymentMethod.id)
        )
        if (storedPaymentMethods && storedPaymentMethods.length > 0) {
            setCardIdSelected(storedPaymentMethods[0].id)
        }
    }, [checkout])

    const handleCardClick = selectedCardId => {
        setIsCollapsed(true)
        setCardIdSelected(selectedCardId)
    }

    useEffect(() => {
        if (cardIdSelected) {
            const storedPaymentMethod = checkout.paymentMethodsResponse.storedPaymentMethods.filter(
                storedPaymentMethod => storedPaymentMethod.id === cardIdSelected
            )[0]
            checkout
                .create('card', storedPaymentMethod)
                .mount(`#stored-card-${storedPaymentMethod.id}`)
        }
    }, [cardIdSelected])

    const cards =
        //$FlowFixMe
        orderState.adyen.paymentMethods?.storedPaymentMethods || []
    const maybeCollapsibleLabel = (
        <H4>
            {intl.formatMessage({ id: 'clickandcollect.payment.cards.new' })}
        </H4>
    )
    const maybeCollapsibleContent = (
        <>
            <Spacer size={SIZE.XS} />
            <div ref={paymentContainer} className="payment" />
        </>
    )
    return (
        <StyledAdyenContainer className="payment-container">
            {!cards.length && (
                <>
                    {maybeCollapsibleLabel}
                    {maybeCollapsibleContent}
                </>
            )}
            {!!cards.length && (
                <FeatureToggle feature={featureCodes.ADYEN_ONE_CLICK}>
                    <H4>
                        {intl.formatMessage({
                            id: 'clickandcollect.payment.cards'
                        })}
                    </H4>
                    <Spacer />
                    {cards.map(storedPaymentMethod => (
                        <CardContainer key={storedPaymentMethod.lastFour}>
                            <CardTextContainer
                                onClick={() =>
                                    handleCardClick(storedPaymentMethod.id)
                                }>
                                <Radio
                                    checked={
                                        storedPaymentMethod.id ===
                                        cardIdSelected
                                    }
                                    color={COLOR.PRIMARY}
                                />
                                <CreditCard />
                                <Text color={COLOR.PRIMARY}>
                                    xxxxxxxxx{storedPaymentMethod.lastFour}
                                    {intl.formatMessage({
                                        id:
                                            'clickandcollect.payment.cards.expiration'
                                    })}
                                    {storedPaymentMethod.expiryMonth} /{' '}
                                    {storedPaymentMethod.expiryYear}
                                </Text>
                            </CardTextContainer>
                            {cardIdSelected === storedPaymentMethod.id && (
                                <StoredCardContainer
                                    id={`stored-card-${storedPaymentMethod.id}`}
                                />
                            )}
                        </CardContainer>
                    ))}
                    <Spacer />
                    <Collapsible
                        isOpened={!isCollapsed}
                        color={oldColors.veryDarkGrey}
                        onClick={() => {
                            setIsCollapsed(!isCollapsed)
                            setCardIdSelected(undefined)
                        }}
                        label={maybeCollapsibleLabel}>
                        {maybeCollapsibleContent}
                    </Collapsible>
                </FeatureToggle>
            )}
        </StyledAdyenContainer>
    )
}

const StoredCardContainer = styled.div`
    padding: 0 ${SPACING.M};
    margin-bottom: ${SPACING.M};
`

const CardContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const CardTextContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
`

const StyledAdyenContainer = styled.div`
    .adyen-checkout__label__text,
    .adyen-checkout__checkbox__label {
        font-size: 18px;
        line-height: 30px;
        font-family: ${fonts.roboto};
        color: ${COLOR.PRIMARY};
    }

    .adyen-checkout__checkbox__label {
        line-height: 16px;
    }

    .adyen-checkout__input {
        height: 40px;
        border: 1px solid ${colors.lightGrey};
        border-radius: 10px;
        font-family: ${fonts.roboto};
        caret-color: ${COLOR.PRIMARY};
        color: ${COLOR.PRIMARY};
    }

    .adyen-checkout__input--focus {
        box-shadow: 0 0 2px ${COLOR.PRIMARY};
    }
`
